import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';

import { licenseStatusOptions, groupingByOptions } from './license-report-options';

import { useStipReport } from '../../hooks/use-stip-report';
import { useFetchPolos } from '../../hooks/fetch/use-fetch-polos';
import { useFetchLicenses } from '../../hooks/fetch/use-fetch-licenses';
import { useFetchEmployeesFromSector } from '../../hooks/fetch/use-fetch-employees-from-sector';

import Loading from '../../../../layouts/Loading';
import CustomMaskField from '../../components/temp/CustomMaskField';
import { GridItem } from '../../components/GridItem';
import { ReportSelect } from '../../components/ReportSelect';
import { ReportTextField } from '../../components/ReportTextField';
import { ReportRadioGroup } from '../../components/ReportRadioGroup';
import { ReportDatePicker } from '../../components/ReportDatePicker';
import { ReportMultiSelect } from '../../components/ReportMultiSelect';
import { ReportSectionTitle } from '../../components/ReportSectionTitle';
import { ReportTogglerGroup } from '../../components/ReportTogglerGroup';
import { ReportDocumentField } from '../../components/ReportDocumentField';
import { ReportMultiAutocomplete } from '../../components/ReportMultiAutocomplete';

const CD_LICENCA_EVENTUAL = 16;
const LICENCAS = 'licencas'

const LicenseReport = () => {
  const { handleToggleChange, handleSelectChange, handleRadioChange } = useStipReport();
  const { values, setFieldValue } = useFormikContext();

  const { loading: loadingPolos, polos } = useFetchPolos();
  const { loading: loadingLicenses, licenses } = useFetchLicenses();
  const { loading: loadingDepartmentEmployees, employees, departments } = useFetchEmployeesFromSector(LICENCAS);

  const loading = loadingPolos || loadingLicenses;
  const hasEventual = values['servicos'] && values['servicos'].includes(CD_LICENCA_EVENTUAL);
  const noEmployees = employees.length === 0;

  useEffect(() => {
    if (!hasEventual) {
      setFieldValue('polos', []);
    }
    // eslint-disable-next-line
  }, [values.servicos]);

  if (loading) {
    return <Loading />;
  }

  return (
    <GridItem container spacing={3}>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Tipo de licença:</ReportSectionTitle>
        <ReportMultiSelect
          options={licenses}
          name="servicos"
          label="Selecione o tipo de licença"
          labelProperty="nmServico"
          valueProperty="cdServico"
          onSelect={handleSelectChange}
        />
      </GridItem>

      {hasEventual && (
        <GridItem sm={6}>
          <ReportSectionTitle gutterBottom>Licença por viagem eventual:</ReportSectionTitle>
          <ReportMultiSelect
            options={polos}
            name="polos"
            label="Selecione o(s) polo(s)"
            valueProperty="cdMunicipio"
            labelProperty="nmMunicipio"
            onSelect={handleSelectChange}
          />
        </GridItem>
      )}

      <GridItem>
        <ReportSectionTitle divider>Opções de filtro:</ReportSectionTitle>
      </GridItem>
      <GridItem>
        <ReportSectionTitle>Data de emissão da licença:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={3}>
        <ReportDatePicker name="dtEmissao.dtInicio" label="Início" />
      </GridItem>
      <GridItem sm={3}>
        <ReportDatePicker name="dtEmissao.dtFim" label="Fim" />
      </GridItem>
      <GridItem>
        <ReportSectionTitle>Data de vencimento da licença:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={3}>
        <ReportDatePicker name="dtVencimento.dtInicio" label="Início" />
      </GridItem>
      <GridItem sm={3}>
        <ReportDatePicker name="dtVencimento.dtFim" label="Fim" />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider>Veículo:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={3}>
        <ReportTextField name="dePlaca" label="Número da placa" />
      </GridItem>
      <GridItem>
        <ReportSectionTitle>Operador:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={6}>
        <ReportTextField name="nmOperador" label="Nome do operador" />
      </GridItem>
      <GridItem sm={3}>
        <ReportDocumentField name="nuDocumentoOperador" label="CPF ou CNPJ do operador" />
      </GridItem>
      <GridItem>
        <ReportSectionTitle>Proprietário:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={6}>
        <ReportTextField name="nmProprietario" label="Nome do proprietario" />
      </GridItem>
      <GridItem sm={3}>
        <ReportDocumentField name="nuDocumentoProprietario" label="CPF ou CNPJ do proprietario" />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider/>
      </GridItem>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Selecione o(s) setor(s):</ReportSectionTitle>
        <ReportMultiAutocomplete
          options={departments}
          disabled={loadingDepartmentEmployees}
          onSelect={handleSelectChange}
          label="Todos os setores"
          name="setores"
          valueProperty="cdOrgaoSetor"
          labelProperty="nmOrgaoSetor"
          getOptionLabel={option => `${option.sgOrgaoSetor} - ${option.nmOrgaoSetor}`}
        />
      </GridItem>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Analista:</ReportSectionTitle>
        <ReportMultiSelect
          options={employees}
          loading={loadingDepartmentEmployees}
          disabled={loadingDepartmentEmployees || noEmployees}
          name="usuarios"
          label={noEmployees ? 'Todos os analistas' : 'Selecione o analista'}
          labelProperty="nmUsuario"
          valueProperty="cdUsuario"
          onSelect={handleSelectChange}
          selectAll
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider />
      </GridItem>
      <GridItem sm={3}>
        <ReportSectionTitle gutterBottom>Solicitação:</ReportSectionTitle>
        <ReportTextField
          component={CustomMaskField}
          mask="999999/9999"
          name="nuSolicitacao"
          label="Número da solicitação"
        />
      </GridItem>
      <GridItem sm={3}>
        <ReportSectionTitle gutterBottom>Licença:</ReportSectionTitle>
        <ReportTextField
          component={CustomMaskField}
          name="nuLicenca"
          mask="999999/9999"
          label="Número da licença"
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider />
      </GridItem>
      <GridItem>
        <ReportRadioGroup
          label="Situação da licença:"
          options={licenseStatusOptions}
          name={'situacaoCrc'}
          onSelect={handleRadioChange}
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider>Opções de agrupamento:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={6}>
        <ReportSelect
          options={groupingByOptions}
          name="agrupamento"
          onSelect={handleSelectChange}
          label={'Selecione o agrupamento'}
        />
      </GridItem>
      <GridItem>
        <ReportSectionTitle divider>Outras opções:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={6}>
        <ReportTogglerGroup
          options={[{ label: 'Exibir resumo', name: 'boExibirResumo' }]}
          onToggle={handleToggleChange}
        />
      </GridItem>
    </GridItem>
  );
};


export default LicenseReport;
